import { getIncidentReportSeverityLabel } from '@features/logs/utils/incidentSeverityUtils'
import type { BadgeColour } from '@lmc/design-system'
import type { BodyMapReport, Home, IncidentReport } from '@lmc/ts-common'
import type { DamageEntry, IncidentCaseV2Step } from '@lmc/validations'
import type {
  PdfBlock,
  PdfDocument,
  PdfSection
} from 'components/pdf/document/pdfDocument'
import type { TFunction } from 'i18next'
import { incidentReportsConstants } from 'locale/incidentReports'
import {
  actionAttachments,
  type IncidentCaseAttachment,
  writtenFollowUpAttachments
} from '../attachments/caseAttachments'
import { INCIDENT_CASE_V2_STEPS } from '../incidentCaseV2Steps'
import {
  type ServiceUserRecord,
  serviceUserDetails
} from '../serviceUserDetails'
import { STATUS_LABEL_KEYS as ACTION_STATUS_LABEL_KEYS } from '../steps/followUpActions/FollowUpActionDrawer'
import { step5Answers } from '../steps/injuriesAndDamages/appInjuries'
import { DAMAGE_TYPE_KEYS } from '../steps/injuriesAndDamages/DamageEntries'
import {
  FACTOR_KEYS,
  GROUP_KEYS
} from '../steps/investigation/ContributingFactors'
import type { CasePeople } from '../steps/PeopleInvolvedStep'
import {
  actionDueDate,
  type CaseMetadata,
  candourDetails,
  candourMethodLabels,
  closure,
  DEBRIEF_KEYS,
  debriefRequiredKey,
  detailsUnderNo,
  embeddedNames,
  enquiryOutcomeKey,
  handoverKey,
  hasWrittenFollowUp,
  type InjuriesCase,
  type InvestigationCase,
  immediateActionLabels,
  injuredPerson,
  injuryBodyMapIds,
  injuryEntries,
  injuryTitle,
  keyRequirementLabels,
  linkedBodyMap,
  namesOfIds,
  notifiedPartyEntries,
  personsInformedLabels,
  practiceValues,
  recordedDamages,
  recordedDebriefs,
  recordedPractices,
  restrictiveDetails,
  SEVERITY_COLOURS,
  STATUS_COLOURS,
  STATUS_LABEL_KEYS,
  savedDutyOfCandour,
  savedFollowUpActions,
  savedRestrictivePractices,
  serviceUserName,
  showsEnquiry,
  tickedFactorGroups,
  toEntryNames,
  toNames,
  yesNo
} from '../view/readRules'
import {
  formatDate,
  formatDateOnly,
  formatDateTime,
  formatTime
} from '../view/readValues'
import type { PersonName } from '../view/usePersonName'
import type { CaseImages } from './caseImages'

/** Everything the document reads besides the case: names, words and settings. */
export type IncidentCaseDocumentContext = {
  bodyMaps: ReadonlyArray<BodyMapReport>
  home: Home | undefined
  /** The group the home belongs to, when the session knows it. */
  homeGroupName: string | undefined
  /** Pictures fetched for printing: image attachments and the GPS map. */
  images: CaseImages
  /** The service user's photo as a data URL, cropped to a circle. */
  photo: string | undefined
  /** The case's service user, when their record has loaded. */
  resident: ServiceUserRecord | undefined
  serviceUserName: PersonName
  serviceUserWords: { plural: string; singular: string }
  t: TFunction
  teamMemberName: PersonName
}

type Context = IncidentCaseDocumentContext
type Value = string | ReadonlyArray<string> | null | undefined

/** A locale key, or a label built from the context and the answer's source. */
type Label<Source> = string | ((context: Context, source: Source) => string)

/** One definition: the blocks it adds for a source, none when unanswered. */
type Definition<Source> = (source: Source, context: Context) => Array<PdfBlock>

const labelOf = <Source>(
  label: Label<Source>,
  context: Context,
  source: Source
) => (typeof label === 'string' ? context.t(label) : label(context, source))

const isBlank = (value: string | null | undefined): value is null | undefined =>
  !value?.trim()

/** A short answer beside its label; an array is a bulleted list. */
const field =
  <Source>(
    label: Label<Source>,
    value: (source: Source, context: Context) => Value
  ): Definition<Source> =>
  (source, context) => {
    const answer = value(source, context)
    if (Array.isArray(answer)) {
      const items = answer.filter((item) => !isBlank(item))
      return items.length
        ? [{ items, kind: 'list', label: labelOf(label, context, source) }]
        : []
    }
    return isBlank(answer as string | null | undefined)
      ? []
      : [
          {
            kind: 'row',
            label: labelOf(label, context, source),
            value: answer as string
          }
        ]
  }

/** An answer shown as a badge in its colour, e.g. a status. */
const badge =
  <Source>(
    label: Label<Source>,
    value: (
      source: Source,
      context: Context
    ) => { colour: BadgeColour; text: string } | null
  ): Definition<Source> =>
  (source, context) => {
    const answer = value(source, context)
    return answer
      ? [
          {
            colour: answer.colour,
            kind: 'row',
            label: labelOf(label, context, source),
            value: answer.text
          }
        ]
      : []
  }

/** Free text under its label, keeping its line breaks. */
const text =
  <Source>(
    label: Label<Source>,
    value: (source: Source, context: Context) => string | null | undefined
  ): Definition<Source> =>
  (source, context) => {
    const answer = value(source, context)
    return isBlank(answer)
      ? []
      : [
          {
            kind: 'text',
            label: labelOf(label, context, source),
            value: answer
          }
        ]
  }

const blocksOf = <Source>(
  definitions: ReadonlyArray<Definition<Source>>,
  source: Source,
  context: Context
) => definitions.flatMap((definition) => definition(source, context))

/**
 * Files under their label: pictures printed, anything else (or a picture
 * that couldn't be fetched) by name.
 */
const attachments =
  <Source>(
    label: Label<Source>,
    filesOf: (source: Source) => ReadonlyArray<IncidentCaseAttachment>
  ): Definition<Source> =>
  (source, context) => {
    const files = filesOf(source)
    const pictures = files.flatMap((file) => {
      const src = context.images.attachments.get(file.key)
      return src ? [{ caption: file.name, src }] : []
    })
    const named = files
      .filter((file) => !context.images.attachments.has(file.key))
      .map((file) => file.name)
    const labelText = labelOf(label, context, source)
    return [
      ...(named.length
        ? [{ items: named, kind: 'list' as const, label: labelText }]
        : []),
      ...(pictures.length
        ? [
            {
              images: pictures,
              kind: 'images' as const,
              // Once is enough when the names come first.
              ...(named.length ? {} : { label: labelText })
            }
          ]
        : [])
    ]
  }

/** A titled group of answers, left out when none are recorded. */
const group =
  <Source>(
    title: Label<Source>,
    definitions: ReadonlyArray<Definition<Source>>
  ): Definition<Source> =>
  (source, context) => {
    const blocks = blocksOf(definitions, source, context)
    return blocks.length
      ? [{ blocks, heading: labelOf(title, context, source), kind: 'entry' }]
      : []
  }

/**
 * One entry per recorded item, e.g. a practice. An item is an answer in
 * itself, so one with nothing under it is its heading alone.
 */
const entries =
  <Source, Item>({
    definitions,
    heading,
    items
  }: {
    definitions: ReadonlyArray<Definition<Item>>
    heading: (item: Item, index: number, context: Context) => string
    items: (source: Source, context: Context) => ReadonlyArray<Item>
  }): Definition<Source> =>
  (source, context) =>
    items(source, context).map((item, index): PdfBlock => {
      const blocks = blocksOf(definitions, item, context)
      const title = heading(item, index, context)
      return blocks.length
        ? { blocks, heading: title, kind: 'entry' }
        : { kind: 'line', text: title }
    })

/** Definitions that apply only when `predicate` holds. */
const when =
  <Source>(
    predicate: (source: Source) => boolean,
    definitions: ReadonlyArray<Definition<Source>>
  ): Definition<Source> =>
  (source, context) =>
    predicate(source) ? blocksOf(definitions, source, context) : []

const metadataOf = (report: IncidentReport) =>
  report as IncidentReport & CaseMetadata

// Step 1: the case at a glance, in the order the view shows it, then groups.
const incidentInformation: ReadonlyArray<Definition<IncidentReport>> = [
  field(
    incidentReportsConstants.CASE_V2_INFO_REFERENCE_ID,
    (report) => metadataOf(report).caseReference
  ),
  field(
    incidentReportsConstants.CASE_V2_INFO_CASE_OWNER,
    (report) => metadataOf(report).caseOwner?.name
  ),
  badge(incidentReportsConstants.CASE_V2_VIEW_STATUS, (report, { t }) => ({
    colour: STATUS_COLOURS[report.status],
    text: t(STATUS_LABEL_KEYS[report.status])
  })),
  badge(
    incidentReportsConstants.CASE_V2_INFO_INCIDENT_SEVERITY,
    (report, { home, t }) =>
      report.severity
        ? {
            colour: SEVERITY_COLOURS[report.severity],
            text: getIncidentReportSeverityLabel(
              report.severity,
              home?.incidentSeverity,
              t
            )
          }
        : null
  ),
  field(
    incidentReportsConstants.CASE_V2_INFO_INCIDENT_TYPE,
    (report) => report.log.item?.name
  ),
  field(
    incidentReportsConstants.CASE_V2_INFO_TITLE,
    (report) => report.log.title
  ),
  field(
    incidentReportsConstants.CLOSED_BY,
    (report) => closure(report)?.closedBy
  ),
  text(
    incidentReportsConstants.CLOSE_COMMENT,
    (report) => closure(report)?.closeComment
  ),
  group(incidentReportsConstants.CASE_V2_INFO_INCIDENT_TIME_AND_DETAILS, [
    field(incidentReportsConstants.CASE_V2_VIEW_INCIDENT_DATE, (report) =>
      formatDate(metadataOf(report).incidentOccurredAt)
    ),
    field(incidentReportsConstants.CASE_V2_VIEW_INCIDENT_TIME, (report) =>
      formatTime(metadataOf(report).incidentOccurredAt)
    ),
    field(
      incidentReportsConstants.CASE_V2_VIEW_INCIDENT_LOCATION,
      (report) => report.log.location
    ),
    (_report, { images, t }) =>
      images.gpsMap
        ? [
            {
              images: [{ src: images.gpsMap }],
              kind: 'images',
              label: t(incidentReportsConstants.GPS_LOCATION)
            }
          ]
        : [],
    text(
      incidentReportsConstants.CASE_V2_VIEW_INCIDENT_DESCRIPTION,
      (report) => report.log.description
    )
  ]),
  group(incidentReportsConstants.CASE_V2_VIEW_LOG_DETAILS, [
    field(incidentReportsConstants.CASE_V2_VIEW_LOGGED_DATE, (report) =>
      formatDate(report.log.timeLogged)
    ),
    field(incidentReportsConstants.CASE_V2_VIEW_LOGGED_TIME, (report) =>
      formatTime(report.log.timeLogged)
    ),
    field(
      incidentReportsConstants.CASE_V2_INFO_LOGGED_BY,
      (report) => report.log.loggedBy?.name
    ),
    field(
      incidentReportsConstants.WITNESSED_BY,
      (report) => report.log.witnessedBy?.name
    ),
    field(incidentReportsConstants.CASE_V2_INFO_LINKED_GOALS, (report) =>
      report.log.goals?.map((goal) => goal.title)
    ),
    field(incidentReportsConstants.CASE_V2_INFO_HANDOVER, (report, { t }) => {
      const key = handoverKey(report.log.isNextHandover)
      return key && t(key)
    })
  ])
]

const immediateActions: ReadonlyArray<Definition<IncidentReport>> = [
  field(
    incidentReportsConstants.CASE_V2_IMMEDIATE_ACTIONS_TAKEN,
    (report, { t }) => immediateActionLabels(t, report)
  )
]

const peopleOf = (report: IncidentReport) => (report.case ?? {}) as CasePeople

const peopleInvolved: ReadonlyArray<Definition<IncidentReport>> = [
  field(
    ({ serviceUserWords, t }) =>
      t(incidentReportsConstants.CASE_V2_VIEW_PEOPLE_SERVICE_USER, {
        variable: serviceUserWords.singular
      }),
    (report, { resident, t }) =>
      serviceUserName(t, report.serviceUser.name, resident?.preferredName)
  ),
  field(
    ({ serviceUserWords, t }) =>
      t(incidentReportsConstants.CASE_V2_PEOPLE_OTHER_SERVICE_USERS, {
        variable: serviceUserWords.plural
      }),
    (report, context) =>
      toNames(peopleOf(report).serviceUsersInvolved, context.serviceUserName)
  ),
  field(
    incidentReportsConstants.CASE_V2_PEOPLE_TEAM_MEMBERS,
    (report, context) =>
      toNames(peopleOf(report).carersInvolved, context.teamMemberName)
  ),
  field(incidentReportsConstants.CASE_V2_PEOPLE_OTHER_PARTIES, (report) =>
    toEntryNames(
      peopleOf(report).otherPartiesInvolved,
      peopleOf(report).otherPeopleInvolved
    )
  ),
  field(incidentReportsConstants.CASE_V2_PEOPLE_WITNESSES, (report) =>
    toEntryNames(peopleOf(report).witnesses, peopleOf(report).otherWitnesses)
  )
]

type Practice = ReturnType<typeof recordedPractices>[number]

const practice =
  (value: (values: ReturnType<typeof practiceValues>) => Value) =>
  (item: Practice, { t }: Context) =>
    value(practiceValues(t, item))

const restrictivePractices: ReadonlyArray<Definition<IncidentReport>> = [
  field(
    incidentReportsConstants.CASE_V2_RESTRICTIVE_ANY_USED,
    (report, { t }) =>
      yesNo(t, savedRestrictivePractices(report)?.hasBeenApplied)
  ),
  text(incidentReportsConstants.CASE_V2_VIEW_RESTRICTIVE_DETAILS, (report) =>
    restrictiveDetails(savedRestrictivePractices(report))
  ),
  entries<IncidentReport, Practice>({
    definitions: [
      field(
        incidentReportsConstants.CASE_V2_VIEW_RESTRICTIVE_TYPE,
        practice(({ type }) => type)
      ),
      field(
        incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD,
        practice(({ method }) => method)
      ),
      field(
        incidentReportsConstants.CASE_V2_RESTRICTIVE_DURATION,
        practice(({ duration }) => duration)
      ),
      field(
        incidentReportsConstants.CASE_V2_RESTRICTIVE_DE_ESCALATION_SUMMARY,
        practice(({ deEscalation }) => deEscalation)
      ),
      field(
        incidentReportsConstants.CASE_V2_RESTRICTIVE_INTERVENTION_TYPE,
        practice(({ interventionType }) => interventionType)
      ),
      text(
        incidentReportsConstants.CASE_V2_RESTRICTIVE_REASON,
        (item) => item.reasonForUse
      ),
      field(
        incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME,
        practice(({ outcomes }) => outcomes)
      ),
      text(
        incidentReportsConstants.CASE_V2_RESTRICTIVE_NOTES,
        (item) => item.notes
      )
    ],
    heading: (_item, index, { t }) =>
      t(incidentReportsConstants.CASE_V2_VIEW_RESTRICTIVE_PRACTICE, {
        number: index + 1
      }),
    items: (report) => recordedPractices(savedRestrictivePractices(report))
  })
]

const injuriesCaseOf = (report: IncidentReport) =>
  (report.case ?? {}) as InjuriesCase

type InjuryItem = ReturnType<typeof injuryEntries>[number] & {
  report: IncidentReport
}

/** An injury's person by name, the name People involved embeds first. */
const injuredName = ({ entry, report }: InjuryItem, context: Context) => {
  const person = injuredPerson(entry)
  const nameOf =
    person?.lookup === 'serviceUser'
      ? context.serviceUserName
      : person?.lookup === 'teamMember'
        ? context.teamMemberName
        : null
  return nameOf
    ? nameOf(
        person?.id,
        person?.id
          ? embeddedNames(injuriesCaseOf(report)).get(person.id)
          : undefined
      )
    : person?.name
}

const injuriesAndDamages: ReadonlyArray<Definition<IncidentReport>> = [
  field(
    incidentReportsConstants.CASE_V2_INJURIES_WAS_ANYONE_INJURED,
    (report, { t }) =>
      yesNo(t, step5Answers(injuriesCaseOf(report)).injuries?.hasBeenApplied)
  ),
  text(incidentReportsConstants.CASE_V2_INJURIES_DETAILS, (report) =>
    detailsUnderNo(step5Answers(injuriesCaseOf(report)).injuries)
  ),
  entries<IncidentReport, InjuryItem>({
    definitions: [
      field(
        incidentReportsConstants.CASE_V2_INJURIES_RELATED_BODY_MAPS,
        ({ entry, report }, { bodyMaps, t }) =>
          injuryBodyMapIds(entry, injuriesCaseOf(report)).map(
            (id) => linkedBodyMap(t, id, bodyMaps).label
          )
      ),
      field(incidentReportsConstants.CASE_V2_INJURIES_NAME, injuredName),
      text(
        incidentReportsConstants.CASE_V2_INJURIES_DETAILS,
        ({ entry }) => entry.details
      ),
      field(
        incidentReportsConstants.CASE_V2_INJURIES_TREATMENT_GIVEN,
        ({ entry }, { t }) => yesNo(t, entry.treatmentGiven)
      ),
      field(
        incidentReportsConstants.CASE_V2_INJURIES_BY_WHOM,
        ({ entry, report }, { teamMemberName }) =>
          entry.treatmentGiven && entry.treatmentGivenBy
            ? teamMemberName(
                entry.treatmentGivenBy,
                embeddedNames(injuriesCaseOf(report)).get(
                  entry.treatmentGivenBy
                )
              )
            : null
      ),
      text(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT, ({ entry }) =>
        entry.treatmentGiven ? entry.treatment : null
      )
    ],
    heading: ({ entry, number, report }, _index, { serviceUserWords, t }) =>
      injuryTitle(t, entry, number, {
        serviceUser: report.serviceUser.name,
        serviceUserWord: serviceUserWords.singular
      }),
    items: (report) =>
      injuryEntries(step5Answers(injuriesCaseOf(report)).injuries).map(
        (item) => ({ ...item, report })
      )
  }),
  field(
    incidentReportsConstants.CASE_V2_INJURIES_WAS_ANYTHING_DAMAGED,
    (report, { t }) =>
      yesNo(t, step5Answers(injuriesCaseOf(report)).damages?.hasBeenApplied)
  ),
  text(incidentReportsConstants.CASE_V2_INJURIES_DETAILS, (report) =>
    detailsUnderNo(step5Answers(injuriesCaseOf(report)).damages)
  ),
  entries<IncidentReport, DamageEntry>({
    definitions: [
      field(
        incidentReportsConstants.CASE_V2_INJURIES_DAMAGE_TYPE,
        (damage, { t }) => t(DAMAGE_TYPE_KEYS[damage.type])
      ),
      text(
        incidentReportsConstants.CASE_V2_INJURIES_DETAILS,
        (damage) => damage.details
      )
    ],
    heading: (_damage, index, { t }) =>
      t(incidentReportsConstants.CASE_V2_VIEW_INJURIES_DAMAGE, {
        number: index + 1
      }),
    items: (report) =>
      recordedDamages(step5Answers(injuriesCaseOf(report)).damages)
  })
]

const followUpActions: ReadonlyArray<Definition<IncidentReport>> = [
  entries({
    definitions: [
      text(
        incidentReportsConstants.CASE_V2_VIEW_ACTIONS_DETAILS,
        (action) => action.details
      ),
      field(
        incidentReportsConstants.CASE_V2_ACTIONS_OWNER,
        (action, { teamMemberName }) => teamMemberName(action.assignedUserId)
      ),
      field(
        incidentReportsConstants.CASE_V2_ACTIONS_STATUS,
        (action, { t }) => {
          const key = ACTION_STATUS_LABEL_KEYS[action.status]
          return key ? t(key) : null
        }
      ),
      field(
        incidentReportsConstants.CASE_V2_ACTIONS_DUE_DATE,
        (action, { t }) => actionDueDate(t, action).text
      ),
      field(
        incidentReportsConstants.CASE_V2_ACTIONS_COMPLETION_TIME,
        (action) => formatDateTime(action.completedAt)
      ),
      attachments(incidentReportsConstants.CASE_V2_ATTACHMENTS, (action) =>
        actionAttachments(action)
      )
    ],
    heading: (action, index, { t }) =>
      t(incidentReportsConstants.CASE_V2_VIEW_ACTIONS_ACTION, {
        number: index + 1,
        title: action.title
      }),
    items: savedFollowUpActions
  })
]

const investigationOf = (report: IncidentReport) =>
  (report.case ?? {}) as InvestigationCase

type DebriefItem = ReturnType<typeof recordedDebriefs>[number]

/** The ticked factors, a list per group, under one heading. */
const contributingFactors: Definition<IncidentReport> = (report, { t }) => {
  const groups = tickedFactorGroups(
    investigationOf(report).contributingFactors ?? []
  )
  return groups.length
    ? [
        {
          blocks: groups.map(({ group: factorGroup, ticked }) => ({
            items: ticked.map((factor) => t(FACTOR_KEYS[factor])),
            kind: 'list' as const,
            label: t(GROUP_KEYS[factorGroup])
          })),
          heading: t(
            incidentReportsConstants.CASE_V2_INVESTIGATION_CONTRIBUTING_FACTORS
          ),
          kind: 'entry'
        }
      ]
    : []
}

const investigation: ReadonlyArray<Definition<IncidentReport>> = [
  field(
    incidentReportsConstants.CASE_V2_VIEW_INVESTIGATION_DEBRIEF_REQUIRED,
    (report, { t }) => {
      const key = debriefRequiredKey(investigationOf(report))
      return key && t(key)
    }
  ),
  entries<IncidentReport, DebriefItem>({
    definitions: [
      text(
        ({ t }, { kind }) => t(DEBRIEF_KEYS[kind].notesKey),
        ({ debrief }) => debrief.notes
      ),
      text(
        incidentReportsConstants.CASE_V2_INVESTIGATION_IMPROVEMENT_ACTIONS,
        ({ debrief, kind }) =>
          DEBRIEF_KEYS[kind].hasImprovementActions
            ? debrief.preventativeActions
            : null
      ),
      field(
        incidentReportsConstants.CASE_V2_INVESTIGATION_ATTENDEES,
        ({ debrief }, { teamMemberName }) =>
          namesOfIds(debrief.debriefedUsers, teamMemberName)
      ),
      field(
        incidentReportsConstants.CASE_V2_INVESTIGATION_COMPLETED_DATE,
        ({ debrief }) => formatDateOnly(debrief.conductedAt)
      )
    ],
    heading: ({ kind }, _index, { t }) => t(DEBRIEF_KEYS[kind].titleKey),
    items: (report) => recordedDebriefs(investigationOf(report))
  }),
  group(incidentReportsConstants.CASE_V2_INVESTIGATION_ROOT_CAUSE_ANALYSIS, [
    text(
      incidentReportsConstants.CASE_V2_INVESTIGATION_IMMEDIATE_CAUSE,
      (report) => investigationOf(report).immediateCause
    ),
    contributingFactors,
    text(
      incidentReportsConstants.CASE_V2_INVESTIGATION_EXPLANATION,
      (report) => investigationOf(report).contributingFactorsExplanation
    ),
    text(
      ({ serviceUserWords, t }) =>
        t(incidentReportsConstants.CASE_V2_INVESTIGATION_SERVICE_USER_OPINION, {
          serviceUser: serviceUserWords.singular
        }),
      (report) => investigationOf(report).serviceUserOpinion
    ),
    text(
      incidentReportsConstants.CASE_V2_INVESTIGATION_LESSONS_IDENTIFIED,
      (report) => investigationOf(report).lessonsIdentified
    )
  ])
]

const candourOf = savedDutyOfCandour

const dutyOfCandour: ReadonlyArray<Definition<IncidentReport>> = [
  field(incidentReportsConstants.CASE_V2_CANDOUR_APPLICABLE, (report, { t }) =>
    yesNo(t, candourOf(report)?.isApplicable)
  ),
  text(incidentReportsConstants.CASE_V2_CANDOUR_DETAILS, (report) =>
    candourDetails(candourOf(report))
  ),
  when(
    (report) => candourOf(report)?.isApplicable === true,
    [
      field(
        incidentReportsConstants.CASE_V2_CANDOUR_PERSON_INFORMED,
        (report, { serviceUserWords, t }) => {
          const saved = candourOf(report)
          return saved
            ? personsInformedLabels(t, saved, serviceUserWords.singular)
            : null
        }
      ),
      group(incidentReportsConstants.CASE_V2_CANDOUR_COMMUNICATION_DETAILS, [
        field(incidentReportsConstants.CASE_V2_CANDOUR_DATE, (report) =>
          formatDate(candourOf(report)?.communication?.communicatedAt)
        ),
        field(incidentReportsConstants.CASE_V2_CANDOUR_TIME, (report) =>
          formatTime(candourOf(report)?.communication?.communicatedAt)
        ),
        field(
          incidentReportsConstants.CASE_V2_CANDOUR_METHOD,
          (report, { t }) =>
            candourMethodLabels(t, candourOf(report)?.communication)
        ),
        field(
          incidentReportsConstants.CASE_V2_CANDOUR_COMMUNICATED_BY,
          (report, { teamMemberName }) =>
            namesOfIds(
              candourOf(report)?.communication?.communicatedBy,
              teamMemberName
            )
        ),
        field(
          incidentReportsConstants.CASE_V2_CANDOUR_KEY_REQUIREMENTS,
          (report, { t }) =>
            keyRequirementLabels(t, candourOf(report)?.communication)
        ),
        field(
          incidentReportsConstants.CASE_V2_CANDOUR_DATE_PROVIDED,
          (report) => {
            const communication = candourOf(report)?.communication
            return hasWrittenFollowUp(communication)
              ? formatDateOnly(communication?.writtenFollowUpProvidedAt)
              : null
          }
        ),
        attachments(incidentReportsConstants.CASE_V2_ATTACHMENTS, (report) => {
          const communication = candourOf(report)?.communication
          return hasWrittenFollowUp(communication)
            ? writtenFollowUpAttachments(communication)
            : []
        }),
        text(
          incidentReportsConstants.CASE_V2_CANDOUR_NOTES,
          (report) => candourOf(report)?.communication?.notes
        )
      ]),
      group(incidentReportsConstants.CASE_V2_CANDOUR_OUTCOME_SHARED, [
        field(incidentReportsConstants.CASE_V2_CANDOUR_DATE_SHARED, (report) =>
          formatDateOnly(candourOf(report)?.outcomeShared?.sharedAt)
        ),
        field(
          incidentReportsConstants.CASE_V2_CANDOUR_PERSON_RESPONSIBLE,
          (report, { teamMemberName }) =>
            teamMemberName(candourOf(report)?.outcomeShared?.responsibleUserId)
        ),
        text(
          incidentReportsConstants.CASE_V2_CANDOUR_NOTES,
          (report) => candourOf(report)?.outcomeShared?.notes
        ),
        field(
          incidentReportsConstants.CASE_V2_VIEW_CANDOUR_COMPLETED_BY,
          (report, { teamMemberName }) =>
            teamMemberName(candourOf(report)?.completedBy)
        ),
        field(
          incidentReportsConstants.CASE_V2_CANDOUR_DATE_COMPLETED,
          (report) => formatDateOnly(candourOf(report)?.completedAt)
        )
      ])
    ]
  )
]

type NotifiedParty = ReturnType<typeof notifiedPartyEntries>[number]

const notifications: ReadonlyArray<Definition<IncidentReport>> = [
  entries<IncidentReport, NotifiedParty>({
    definitions: [
      field(
        ({ t }, { definition }) => t(definition.dateLabelKey),
        ({ entry }) => formatDateOnly(entry.notifiedAt)
      ),
      field(
        ({ t }, { definition }) => t(definition.byLabelKey),
        ({ entry }, { teamMemberName }) => teamMemberName(entry.notifiedBy)
      ),
      field(
        incidentReportsConstants.CASE_V2_NOTIFICATIONS_REFERENCE_NUMBER,
        ({ definition, entry }) =>
          definition.hasReferenceNumber ? entry.referenceNumber : null
      ),
      field(
        incidentReportsConstants.CASE_V2_NOTIFICATIONS_PROGRESSED_TO_ENQUIRY,
        ({ definition, entry }, { t }) =>
          definition.hasEnquiry ? yesNo(t, entry.progressedToEnquiry) : null
      ),
      when(showsEnquiry, [
        field(
          incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_TYPE,
          ({ entry }) => entry.enquiryType
        ),
        field(
          incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_LOCAL_AUTHORITY,
          ({ entry }) => entry.enquiryLocalAuthority
        ),
        field(
          incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME,
          (notified, { t }) => {
            const key = enquiryOutcomeKey(notified)
            return key && t(key)
          }
        ),
        text(
          incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME_RECOMMENDATION,
          ({ entry }) => entry.enquiryOutcomeAndRecommendation
        ),
        field(
          incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_CLOSED_AT,
          ({ entry }) => formatDateOnly(entry.enquiryClosedAt)
        )
      ])
    ],
    heading: ({ definition }, _index, { t }) => t(definition.labelKey),
    items: notifiedPartyEntries
  })
]

/** Each step's answers, in the order the case view shows them. */
const SECTIONS: Record<
  IncidentCaseV2Step,
  ReadonlyArray<Definition<IncidentReport>>
> = {
  dutyOfCandour,
  followUpActions,
  immediateActions,
  incidentInformation,
  injuriesAndDamages,
  investigation,
  notifications,
  peopleInvolved,
  restrictivePractices
}

/**
 * Who the case is about, as the details card shows them. Without their
 * record, the name the report embeds stands alone rather than print every
 * detail as not recorded.
 */
const serviceUserSection = (
  report: IncidentReport,
  { photo, resident, serviceUserWords, t }: Context
): PdfSection => {
  const heading = t(incidentReportsConstants.CASE_V2_SERVICE_USER_DETAILS, {
    variable: serviceUserWords.singular
  })
  if (!resident) {
    return {
      blocks: [{ kind: 'person', name: report.serviceUser.name, photo }],
      heading
    }
  }
  const details = serviceUserDetails(t, resident)
  const orNotRecorded = (value: string | null | undefined) =>
    value || t(incidentReportsConstants.CASE_V2_SU_NOT_RECORDED)
  const detail = (labelKey: string, value: string | null | undefined) => ({
    kind: 'row' as const,
    label: t(labelKey),
    value: orNotRecorded(value)
  })

  return {
    blocks: [
      {
        kind: 'person',
        name: details.name || report.serviceUser.name,
        photo
      },
      detail(incidentReportsConstants.CASE_V2_SU_SEX, details.sex),
      detail(
        incidentReportsConstants.CASE_V2_SU_DATE_OF_BIRTH,
        details.dateOfBirth
      ),
      detail(incidentReportsConstants.CASE_V2_SU_NHS_NUMBER, details.nhsNumber),
      {
        kind: 'text',
        label: t(incidentReportsConstants.CASE_V2_SU_ADDRESS),
        value: orNotRecorded(details.address)
      }
    ],
    heading
  }
}

/**
 * A v2 case as a document: titled by the incident's type with its status,
 * under the home's and group's names, then the service user's details and a
 * section per step with any answers, in step order. Unanswered questions and
 * empty sections are left out; a person who can't be found reads as the
 * view shows them.
 */
export const buildIncidentCaseV2Document = (
  report: IncidentReport,
  context: Context
): PdfDocument => ({
  footer: context.t(incidentReportsConstants.INCIDENT_CASE),
  header: { end: context.homeGroupName, start: context.home?.name },
  pageLabel: (page) =>
    context.t(incidentReportsConstants.CASE_V2_PDF_PAGE, { page }),
  sections: [
    serviceUserSection(report, context),
    ...INCIDENT_CASE_V2_STEPS.flatMap(({ id, titleKey }) => {
      const blocks = blocksOf(SECTIONS[id], report, context)
      return blocks.length ? [{ blocks, heading: context.t(titleKey) }] : []
    })
  ],
  title: context.t(incidentReportsConstants.CASE_V2_PDF_TITLE, {
    type: report.log.item?.name ?? report.log.title
  }),
  titleBadge: {
    colour: STATUS_COLOURS[report.status],
    text: context.t(STATUS_LABEL_KEYS[report.status])
  }
})
